<template>
    <ElementLayout ref="container">
        <template #element>
            <div :class="[classes.inputContainer, { 'iti-is-readonly': isReadonly || isDisabled }]" ref="rowWrapper">
                <div :class="classes.optionsWrapper">
                    <ElementLabelFloating v-if="hasFloating && !empty" :visible="!empty" />
                    <ElementLoader v-if="isLoading"/>
                    <!--
                      IntlTelInput builds its full ~245-country dropdown (~980 DOM
                      nodes) synchronously on mount, every time, regardless of
                      whether the dropdown is ever opened — confirmed by reading
                      #appendListItems() in the library and measuring it in isolation
                      (docs/specs/2026-08-06-phone-field-performance.md): linear up to
                      ~75 instances, then a hard cliff. A repeater row mounts the real
                      widget only while it's near the visible viewport (see
                      observeRowVisibility() — a persistent IntersectionObserver on
                      the stable rowWrapper above, tracking both entering AND
                      leaving) and un-mounts back to the plain, identically-styled
                      placeholder once scrolled away, so the DOM cost stays bounded
                      to "rows near the viewport" regardless of list length or how
                      much scrolling happens. Click/focus is kept as an immediate
                      fallback trigger. isValid() below covers rows that never get
                      activated at all (bulk import, preloaded values).
                    -->
                    <!--
                      initialCountry falls back to the country the user last
                      had selected here (rememberedCountry), not straight to
                      preselectCountry: the widget is re-created from scratch on
                      every scroll-in, and a country choice that never made it
                      into `model` (empty or still-incomplete number) would
                      otherwise silently revert to 'de' — and #ensureHasDialCode
                      would then stamp +49 onto what the user entered as an
                      Austrian number. See rememberSelectedCountry() below.
                    -->
                    <IntlTelInput v-if="activated"
                                  :options="{initialCountry: rememberedCountry || preselectCountry, countryOrder: ['de', 'at', 'ch'], countrySearch: false, i18n: $vueform.i18n.locale == 'de' ? de : en, allowedNumberTypes:['FIXED_LINE_OR_MOBILE']}"
                                  :name="name"
                                  :id="fieldId"
                                  :class="classes.input"
                                  :placeholder="Placeholder"
                                  :autocomplete="autocomplete"
                                  :disabled="isDisabled"
                                  :readonly="isReadonly"
                                  v-bind="{
                                    ...attrs,
                                    ...aria,
                                  }"
                                  @keydown="handleKeydown"
                                  @input="handleInput"
                                  @select="handleInput"
                                  @blur="handleBlur"
                                  @focus="handleFocus"
                                  ref="input"
                                  @changeCountry="rememberSelectedCountry"
                                  @changeNumber="setValue"/>
                    <!--
                      Wrapping the fallback input in .iti and giving it the library's
                      own .iti__tel-input class means the existing _phone.scss rules
                      (border, radius, min-height, background, box-shadow) apply
                      unchanged — no separate placeholder stylesheet, and the later
                      swap to the real widget is layout-neutral.
                    -->
                    <div v-else class="iti">
                        <input type="tel"
                               :class="[classes.input, 'iti__tel-input']"
                               :id="fieldId"
                               :name="name"
                               :placeholder="Placeholder"
                               :autocomplete="autocomplete"
                               :disabled="isDisabled"
                               :readonly="isReadonly"
                               :value="phoneDisplayValue"
                               v-bind="{...attrs, ...aria}"
                               @input="handleFallbackInput"
                               @click="activatePhoneWidget()"
                               @focus="activatePhoneWidget()"
                               ref="lazyInput"/>
                    </div>
                </div>
            </div>
        </template>
        <!-- Default element slots -->
        <template v-for="(component, slot) in elementSlots" #[slot]><slot :name="slot" :el$="el$"><component :is="component" :el$="el$"/></slot></template>
    </ElementLayout>
</template>

<script>
import {defineElement } from '@vueform/vueform';
import {shallowRef} from 'vue';
import IntlTelInput from "intl-tel-input/vue/build/exports/IntlTelInputWithUtils.mjs";
import de from "intl-tel-input/build/js/i18n/de/index.js";
import en from "intl-tel-input/build/js/i18n/en/index.js";
import useElement from "@vueform/vueform/src/composables/useElement";
import useEl$ from "@vueform/vueform/src/composables/elements/useEl$";
import useForm$ from "@vueform/vueform/src/composables/useForm$";
import useTheme from "@vueform/vueform/src/composables/useTheme";
import useLayout from "@vueform/vueform/src/composables/elements/useLayout";
import useInput from "@vueform/vueform/src/composables/elements/useInput";
import usePath from "@vueform/vueform/src/composables/elements/usePath";
import useNullValue from "@vueform/vueform/src/composables/elements/useNullValue";
import useFieldId from "@vueform/vueform/src/composables/elements/useFieldId";
import useFloating from "@vueform/vueform/src/composables/elements/useFloating";
import useEvents from "@vueform/vueform/src/composables/useEvents";
import useBaseElement from "@vueform/vueform/src/composables/elements/useBaseElement";
import useDisabled from "@vueform/vueform/src/composables/elements/useDisabled";
import useReadonly from "@vueform/vueform/src/composables/elements/useReadonly";
import {text as useDefault} from "@vueform/vueform/src/composables/elements/useDefault";
import useConditions from "@vueform/vueform/src/composables/useConditions";
import {text as useValidation} from "@vueform/vueform/src/composables/elements/useValidation";
import useLoading from "@vueform/vueform/src/composables/elements/useLoading";
import useValue from "@vueform/vueform/src/composables/elements/useValue";
import useEmpty from "@vueform/vueform/src/composables/elements/useEmpty";
import useData from "@vueform/vueform/src/composables/elements/useData";
import useLabel from "@vueform/vueform/src/composables/elements/useLabel";
import useGenericName from "@vueform/vueform/src/composables/elements/useGenericName";
import useView from "@vueform/vueform/src/composables/elements/useView";
import useTemplates from "@vueform/vueform/src/composables/elements/useTemplates";
import useClasses from "@vueform/vueform/src/composables/elements/useClasses";
import useColumns from "@vueform/vueform/src/composables/elements/useColumns";
import useSlots from "@vueform/vueform/src/composables/elements/useSlots";
import {phone as useHandleInput} from "@vueform/vueform/src/composables/elements/useHandleInput";
import useFocused from "@vueform/vueform/src/composables/elements/useFocused";
import useHandleBlur from "@vueform/vueform/src/composables/elements/useHandleBlur";
import {phone as useA11y} from "@vueform/vueform/src/composables/elements/useA11y";
import useWatchValue from "@vueform/vueform/src/composables/elements/useWatchValue";
import useFocus from "@vueform/vueform/src/composables/elements/useFocus";
import {phone as useHandleKeyEvents} from "@vueform/vueform/src/composables/elements/useHandleKeyEvents";
import usePlaceholder from "@vueform/vueform/src/composables/elements/usePlaceholder";
import BaseElement from "@vueform/vueform/src/mixins/BaseElement";
import HasView from "@vueform/vueform/src/mixins/HasView";
import HasChange from "@vueform/vueform/src/mixins/HasChange";
import HasData from "@vueform/vueform/src/mixins/HasData";
import HasValidation from "@vueform/vueform/src/mixins/HasValidation";

// Lazily loaded once per page (module singleton, shared across every phone
// field), independent of `activated` — mounting the real widget is deferred to
// focus, but a row that never gets focused (common in a large imported
// repeater) still needs a format check in isValid() below.
//
// @gotcha Keep both this and plugins.js on the *same* specifier — two different
// ones for the same package produce two chunks Parcel can't both name
// "libphonenumber-js", and the build fails outright. /max is the historical
// choice: isValid() plus getType() needed the fuller metadata set, since the
// default/min set returns undefined from getType() for every number (verified
// directly: parses and validates fine, type always undefined). isValid() below
// no longer checks the type (see the product decision in isValid()), so /max is
// no longer strictly required here — but switching would have to move both call
// sites at once and revalidate plugins.js's import normalization, which still
// uses isValid(). Not worth doing casually for the bundle-size win alone.
//
// @gotcha A shallowRef, not a plain `let` — phoneDisplayValue() below reads it
// from a computed, and a plain module variable is invisible to Vue's dependency
// tracking. The computed would then cache the result of its very first
// evaluation (the row's first render, which happens *before* mounted() has even
// started the import) and never re-run: since Vue 3.4 a computed only
// re-triggers its dependents when its value actually changes, and a preloaded
// row's `model` never changes — the stored value is already E.164, so the
// widget's setNumber()/getNumber() round-trip hands back the identical string.
// The formatted display would never appear for exactly the rows it exists for.
// shallowRef rather than ref because the value is a function.
const lazyParsePhone = shallowRef(null);
let phoneParserPromise = null;
const ensurePhoneParser = () => {
    if (!phoneParserPromise) {
        phoneParserPromise = import('libphonenumber-js/max')
            .then((mod) => { lazyParsePhone.value = mod.parsePhoneNumberFromString; })
            .catch((e) => {
                // Stay permissive (isValid() below already falls back to
                // `true` when the parser is unset) — but a chunk 404 must not
                // silently degrade every unactivated row's validation forever.
                // console alone is not enough: this bundle's Sentry setup has
                // no captureConsole integration (see sentry.js), so a console
                // line never leaves the user's browser.
                console.error('[PhoneElement] failed to load libphonenumber-js/max', e)
                if (typeof window.reportFewException === 'function') {
                    window.reportFewException(e, {tags: {area: 'phone-element', cause: 'chunk-load'}})
                }
            });
    }
    return phoneParserPromise;
};
export default defineElement({
    mixins: [BaseElement, HasView, HasChange, HasData, HasValidation],
    emits: ['change', 'select', 'open', 'close', 'blur', 'focus', 'beforeCreate', 'created', 'beforeMount', 'mounted', 'beforeUpdate', 'updated', 'beforeUnmount', 'unmounted'],
    props: {
        type: {
            required: false,
            type: [String],
            default: 'text',
            private: true,
        },
        default: {
            required: false,
            type: [String, Number, Object],
            localized: true,
            default: null
        },
        debounce: {
            required: false,
            type: [Number],
            default: null
        },
        disabled: {
            required: false,
            type: [Boolean, Function, Array, Object],
            default: false
        },
        floating: {
            required: false,
            type: [String, Boolean, Object],
            localized: true,
            default: null
        },
        id: {
            required: false,
            type: [String],
            default: null
        },
        placeholder: {
            required: false,
            type: [String, Object],
            localized: true,
            default: null
        },
        readonly: {
            required: false,
            type: [Boolean, Function, Array, Object],
            default: false
        },

        include: {
            required: false,
            type: [Array],
            default: () => ([]),
        },
        exclude: {
            required: false,
            type: [Array],
            default: () => ([]),
        },
        unmask: {
            require: false,
            type: [Boolean],
            default: false,
        },
        allowIncomplete: {
            require: false,
            type: [Boolean],
            default: false,
        },
        attrs: {
            required: false,
            type: [Object],
            default: () => ({}),
        },
        autocomplete: {
            required: false,
            type: [String, Number],
            default: null
        },
        loading: {
            type: [Boolean],
            required: false,
            default: false,
        },

        onBlur: {
            required: false,
            type: [Function],
            default: null,
            private: true,
        },
        onSelect: {
            required: false,
            type: [Function],
            default: null,
            private: true,
        },
        preselectCountry: {
            required: false,
            type: String,
            default: 'de'
        },
    },
    name: 'NewPhoneElement',
    components: {IntlTelInput},
    data() {
        // Only defer mounting inside a repeater row — a standalone phone field
        // is never at the scale where this matters, and eagerly mounting it
        // keeps today's behaviour (preloaded value shown formatted immediately)
        // unchanged there. Walk the full ancestor chain rather than checking a
        // fixed depth: a list whose row IS the phone element directly (no
        // object-type row wrapper in between) has isListType one level closer
        // than the person/crew repeaters this was built against, and a fixed
        // `parent.parent` check would silently skip lazy-mounting for it.
        let insideRepeater = false
        let ancestor = this.parent
        while (ancestor) {
            if (ancestor.isListType) {
                insideRepeater = true
                break
            }
            ancestor = ancestor.parent
        }
        return {
            de,
            en,
            ref: {},
            // Last country this row's widget had selected (whether picked from
            // the dropdown or derived from a typed dial code), kept across the
            // widget's scroll-driven unmount/remount cycles so a remount
            // doesn't fall back to preselectCountry. See the initialCountry
            // binding in the template.
            rememberedCountry: null,
            merge: true,
            initialized: false,
            activated: !insideRepeater,
            insideRepeater,
            defaultClasses: {
                container: '',
                inputContainer: '',
                input: '',
            },
        }
    },
    computed: {
        // Region for display formatting and for the fallback validity check.
        // Derived from the field's own configuration (and the row's remembered
        // country) instead of a hard-coded 'DE', so an instance that presets
        // e.g. 'at' reads a bare national number ("0664 …") as Austrian rather
        // than silently as German — which decides both how it renders and
        // whether isValid() accepts it.
        phoneRegion() {
            return String(this.rememberedCountry || this.preselectCountry || 'de').toUpperCase()
        },
        // The fallback input shows this instead of raw `model`. Without it, a
        // row that deactivates (scrolls out of range) suddenly displays the
        // canonical stored value — typically E.164 ("+4915110646830") — right
        // after the widget had it looking normal ("01511 0646830"). That's a
        // visible reformat on scroll alone, the same "looks broken" complaint
        // this whole lazy-mount effort exists to avoid, just relocated to the
        // deactivate transition instead of the initial mount.
        phoneDisplayValue() {
            if (!this.model) {
                return ''
            }
            const parsePhone = lazyParsePhone.value
            if (!parsePhone) {
                return this.model
            }
            try {
                const parsed = parsePhone(String(this.model), this.phoneRegion)
                if (!parsed) {
                    return this.model
                }
                // National format only for the field's own region. The
                // placeholder has no country flag next to it (the mounted
                // widget does), so a foreign number rendered nationally
                // ("0664 1234567" for an Austrian +43) reads as a local one —
                // international format keeps it unambiguous. For the common
                // case, a number from the field's own region, this matches the
                // widget's own nationalMode display exactly, which is the
                // point: no visible change on scroll.
                return parsed.country === this.phoneRegion
                    ? parsed.formatNational()
                    : parsed.formatInternational()
            } catch (e) {
                return this.model
            }
        },
    },
    watch: {
        model(newVal, oldVal) {
            // Before activation there is no widget to push the value into — the
            // plain placeholder input already shows `model` via its :value binding.
            if (!this.activated || this.initialized) {
                return
            }
            // @gotcha $refs.input can briefly be undefined here: activatePhoneWidget()
            // sets `activated = true` synchronously, but the v-if swap only takes
            // effect on the next render flush. A model change landing in that same
            // flush (e.g. a bulk-import write) would otherwise hit this watcher
            // before the real widget exists.
            const iti = this.liveWidget()
            if (!iti) {
                return
            }
            iti.setNumber(this.widgetNumber(newVal))
            this.initialized = true
        }
    },
    mounted() {
        // Cheap (just fetches a JS chunk, no DOM work) — kicked off for every
        // phone field regardless of `activated` so isValid() has it ready by
        // the time a submit needs to validate an unfocused row.
        ensurePhoneParser()
        if (this.activated) {
            this.applyModelToWidget()
        }
        if (this.insideRepeater) {
            this.observeRowVisibility()
        }
    },
    beforeUnmount() {
        if (this.phoneRowObserver) {
            this.phoneRowObserver.disconnect()
            this.phoneRowObserver = null
        }
    },
    methods: {
        // Persistent (not one-shot) IntersectionObserver on the stable
        // rowWrapper — tracks the row entering AND leaving the visible area
        // for its whole lifetime, so a row scrolled past once and then away
        // again reverts to the plain placeholder instead of staying mounted
        // forever. That's what keeps DOM cost bounded regardless of how much
        // of a long list gets scrolled through, unlike a one-shot "activate
        // once, never deactivate" observer (tried and reverted — it degrades
        // back into "everything mounted eventually" for a full scroll-through).
        //
        // `root` is resolved to the nearest actual scrolling ancestor rather
        // than left as the default (viewport): this form renders inside a
        // scrolling `uk-modal`, and IntersectionObserver's `rootMargin` is
        // only applied against the *root's* bounds — with `root: null` that's
        // the viewport, not the modal's own scroll area, so the intended
        // lead-time never materializes and a row only activates once
        // literally already visible. Confirmed live while debugging this.
        observeRowVisibility() {
            const target = this.$refs.rowWrapper
            if (!target || typeof IntersectionObserver === 'undefined') {
                return
            }
            let node = target.parentElement
            let root = null
            while (node) {
                const style = getComputedStyle(node)
                if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
                    root = node
                    break
                }
                node = node.parentElement
            }
            this.phoneRowObserver = new IntersectionObserver((entries) => {
                const entry = entries[entries.length - 1]
                if (entry.isIntersecting) {
                    // No focusAfter — the row scrolled into range, nobody
                    // clicked it; stealing focus into it would be jarring.
                    this.activatePhoneWidget(false)
                } else {
                    this.deactivatePhoneWidget()
                }
            }, { root, rootMargin: '400px 0px' })
            this.phoneRowObserver.observe(target)
        },
        // Guarded by isRowFocused() — never tear down a row the user is
        // actively typing into or picking a country in, even if it scrolls out
        // of range (e.g. a parent auto-scroll, or the observer firing while
        // mid-edit).
        deactivatePhoneWidget() {
            if (!this.activated || this.isRowFocused()) {
                return
            }
            // @gotcha Must call instance.destroy() ourselves, BEFORE flipping
            // `activated` (which triggers Vue's own v-if removal) — not after,
            // and not left to the library's own onUnmounted hook. Vue's
            // unmount order removes the component's DOM node (here: the
            // <input> itself) synchronously, then queues the `unmounted`
            // hook to run afterwards (Vue's queuePostRenderEffect). By the
            // time the library's own onUnmounted-triggered destroy() runs,
            // Vue has already detached the input from the .iti wrapper
            // generateMarkup() moved it into — so destroy()'s own
            // `wrapper.before(this.telInput)` throws on a null wrapper
            // partway through, and never reaches `wrapper.remove()`. The
            // wrapper (flag, dropdown, everything) is orphaned in the DOM,
            // repeatedly, on every deactivate/reactivate cycle — exactly the
            // "flag drawn multiple times, stacked" symptom found live.
            // Calling destroy() here, while the input is still in its normal
            // wrapped position, lets it move the input out and remove the
            // wrapper cleanly; the library's own later onUnmounted call then
            // finds `#ui.telInput` already null and no-ops.
            const iti = this.liveWidget()
            if (iti) {
                iti.destroy()
            }
            this.activated = false
            // The widget instance that set this is gone; a fresh one needs
            // its own setNumber() call on reactivation (see applyModelToWidget()).
            this.initialized = false
        },
        // @gotcha Must check rowWrapper, not $refs.input.$el. $el IS the <input>
        // itself (see the gotcha in activatePhoneWidget below) — an <input> can
        // never have element children, so el.contains(...) can never be true.
        // The country button and open dropdown are its SIBLINGS inside the
        // .iti wrapper intl-tel-input inserts (not descendants of the input),
        // so this guard never actually caught focus inside them: opening the
        // dropdown and scrolling could tear the row down mid-interaction.
        // rowWrapper contains the whole .iti subtree (button, dropdown,
        // search input, the real <input>), so this covers all of it.
        //
        // @gotcha activeElement alone still isn't enough for the open dropdown,
        // for two reasons found in the library source: with countrySearch
        // disabled (as configured above) opening the dropdown moves focus
        // nowhere at all — it stays on whatever the click focused, and Safari
        // and iOS don't focus a <button> on click — and on a narrow viewport
        // useFullscreenPopup kicks in, which reparents the dropdown to
        // document.body, outside rowWrapper entirely. The flag button itself
        // stays inside rowWrapper in both cases and carries aria-expanded, so
        // that attribute is the reliable "dropdown is open" signal.
        isRowFocused() {
            const wrapper = this.$refs.rowWrapper
            if (!wrapper) {
                return false
            }
            return wrapper.contains(document.activeElement)
                || !!wrapper.querySelector('.iti__selected-country[aria-expanded="true"]')
        },
        // Remembering the country is what keeps a scroll-driven remount from
        // resetting it: the widget only reports a number through changeNumber
        // when getNumber() actually changes, so a country picked on an empty or
        // still-incomplete field never reaches `model` — and an incomplete
        // number is stored without its dial code, which #ensureHasDialCode then
        // completes with the *newly* initialised country on remount. Selecting
        // AT, typing "0664 12", scrolling away and back turned that into a
        // German +49 number.
        rememberSelectedCountry(iso2) {
            if (iso2) {
                this.rememberedCountry = iso2
            }
        },
        // Keeps `model` in sync with the plain fallback input for writers that
        // don't go through click/focus — e.g. browser/password-manager autofill,
        // which can set .value on an unfocused input. Without this, the value is
        // visible in the DOM but never reaches Vueform's data, and gets silently
        // discarded once the row activates and re-renders from `model`.
        handleFallbackInput(e) {
            this.model = e.target.value
        },
        // The model watcher only fires on change, so a value already present
        // when the widget mounts (bulk-imported rows, a preloaded value, or a
        // field that only just activated) never reaches intl-tel-input on its
        // own — push it in explicitly. Shared by mounted() (eager case) and
        // activate() (lazy case, since mounted() already ran once with
        // activated===false and won't run again).
        applyModelToWidget() {
            if (!this.model) {
                return
            }
            const apply = () => {
                const iti = this.liveWidget()
                if (iti && !this.initialized) {
                    iti.setNumber(this.widgetNumber(this.model))
                    this.initialized = true
                }
            }
            if (this.liveWidget()) {
                apply()
            } else {
                this.$nextTick(apply)
            }
        },
        // @gotcha Named activatePhoneWidget, NOT activate — useBaseElement (one of
        // the composables setup() spreads in via useElement) already defines its
        // own `activate()` that just flips an internal `active` flag for tab/step
        // visibility. Setup-exposed bindings win over same-named Options API
        // methods in Vue 3, so a method here called `activate` gets silently
        // shadowed: clicks fire Vueform's activate instead of this one, with no
        // error and no visible effect. This is exactly what the original
        // (reverted) v1.14.27 attempt hit and left unresolved.
        activatePhoneWidget(focusAfter = true) {
            if (this.activated) {
                return
            }
            this.activated = true
            this.$nextTick(() => {
                this.applyModelToWidget()
                if (!focusAfter) {
                    return
                }
                // The plain placeholder <input> the user just clicked/focused
                // gets destroyed by the v-if swap — without this the browser
                // silently drops focus onto <body> mid-click.
                //
                // @gotcha IntlTelInputWithUtils's own Vue template root IS the
                // <input> itself (see node_modules/intl-tel-input/vue/build/
                // IntlTelInputWithUtils.mjs — createElementBlock("input", …)) —
                // intl-tel-input's vanilla-JS _generateMarkup() wraps that same
                // node in extra divs AFTER mount, via plain DOM insertBefore/
                // appendChild outside Vue's tracking, but Vue's own `$el`
                // reference still points at the original (relocated, still
                // attached) <input> node, not at a wrapper. So `$el` already IS
                // the input — querySelector('input') on it looks for an <input>
                // *descendant* of an <input>, which can never exist.
                const iti$el = this.$refs.input && this.$refs.input.$el
                const input = iti$el && (iti$el.tagName === 'INPUT' ? iti$el : iti$el.querySelector('input'))
                if (input) {
                    input.focus()
                }
            })
        },
        // @gotcha The deferred read needs a liveness guard, not just a ref
        // guard: this runs a tick after the widget reported a number, by which
        // time the row can have been deactivated (scroll) or removed
        // (condition, tab, row delete). A destroyed instance's getNumber()
        // returns "" — writing that into `model` would wipe the value the user
        // just typed. isActive() is the library's own "not destroyed" check.
        setValue() {
            window.setTimeout(() => {
                const iti = this.liveWidget()
                if (!iti) {
                    return
                }
                this.model = iti.getNumber()
            })
        },
        // @gotcha Every setNumber() call must be fed a string. The library hands
        // the argument straight to #getCountryFromNumber(), which does
        // `val.indexOf('+')` with no type check — a saved value that arrives as
        // a number (a phone column stored numerically) throws "e.indexOf is not
        // a function" out of the watcher and takes the whole element down with
        // it (Sentry FEW-FRONTEND-VUE-9J). null/undefined has to become '' and
        // not String(null) === "null", which the widget would happily parse as
        // a number and leave in the field.
        widgetNumber(val) {
            return val == null ? '' : String(val)
        },
        // The widget instance, but only while it is really usable. `initialized`
        // and `$refs.input` can both outlive the DOM the instance needs — a
        // hidden condition branch or a switched tab unmounts the element without
        // this component's own deactivate path running.
        liveWidget() {
            const iti = this.$refs.input && this.$refs.input.instance
            return (iti && iti.isActive()) ? iti : null
        },
        isValid() {
            if (!this.model && !this.rules.includes('required')) {
                return true
            }
            // @gotcha Guard before touching the instance. Reading
            // `$refs.input.instance` unguarded here was the most frequent
            // PhoneElement crash in Sentry (issues 7V / AM, 52 users) —
            // `initialized` stays true while the element itself is gone. When
            // the widget isn't live, fall through to the parser check below,
            // which validates the stored value just as strictly.
            const iti = this.initialized ? this.liveWidget() : null
            if (iti) {
                // @gotcha isValidNumber(), NOT isValidNumberPrecise(), and this
                // is a deliberate product decision — see the table in
                // docs/specs/2026-08-06-phone-field-performance.md §0.6.
                // isValidNumber() is only a length-plausibility check
                // (isPossibleNumber under the hood, already so in v25.3.0);
                // Precise runs Google's full validation. Precise was tried in
                // v1.14.32 for consistency with the fallback and reverted one
                // day later: the predecessor plugin (oc-frontend-forms, via
                // intl-tel-input v18.1.1, where isValidNumber() *was* the full
                // check) validated strictly and produced real "I can't enter my
                // phone number" complaints. The bundled metadata still knows no
                // valid length for the mobile prefixes 0153, 0154, 0161, 0165,
                // 0166 and 0167, so strict validation rejects every number on
                // them outright. Whoever reopens this: the scroll-flip bug that
                // motivated Precise is fixed by both paths agreeing, not by
                // them being strict — keep them agreeing.
                return iti.isValidNumber()
            }
            // Empty-but-required is the 'required' rule's job, not this one's —
            // without this, String(null) becomes the literal text "null", fails
            // to parse below, and a required-empty cell in an unactivated row
            // gets a redundant "invalid format" error on top of "required".
            if (!this.model) {
                return true
            }
            // Widget not mounted (row never activated, or scrolled away) — fall
            // back to the same format check the CSV import already uses
            // (plugins.js), against the field's own region (phoneRegion), so an
            // unfocused imported/preloaded row is still validated.
            //
            // @tradeoff If the parser chunk hasn't resolved yet, stay
            // permissive rather than block submit. ensurePhoneParser() starts
            // at every phone field's mounted() — effectively at form load —
            // so by the time a real submit happens the chunk has almost
            // always already arrived; an async validator that awaits it
            // explicitly was tried and reverted (it broke Vueform's
            // initial/tab-level validation pass, showing a spurious error
            // badge before the user touches anything) — not worth it to close
            // a window this narrow.
            const parsePhone = lazyParsePhone.value
            if (!parsePhone) {
                return true
            }
            try {
                const parsed = parsePhone(String(this.model), this.phoneRegion)
                // isPossible(), matching the widget's isValidNumber() above —
                // a length-plausibility check, not full validation.
                //
                // @gotcha No number-type check here any more, and it can't come
                // back in this form: getType() only returns a type for a *fully
                // valid* number, so pairing it with isPossible() would reject
                // every possible-but-not-valid number and make this path strict
                // again — the exact split this pair of checks exists to avoid.
                // The widget path does still restrict types (allowedNumberTypes
                // in its options), but through a type-aware possible-length
                // check that libphonenumber-js does not expose. The residual
                // gap is one-directional and confined to number classes outside
                // mobile/fixed-line (0700, 0180 and similar): this path may
                // accept one the widget rejects, never the other way round.
                return !!(parsed && parsed.isPossible())
            } catch (e) {
                return true
            }
        }
    },
    setup(props, ctx) {
        const context = {...ctx};
        context.features = [
            useEl$,
            useForm$,
            useTheme,
            useLayout,
            useInput,
            usePath,
            useNullValue,
            useFieldId,
            useFloating,
            useEvents,
            useBaseElement,
            useDisabled,
            useReadonly,
            useDefault,
            useConditions,
            useValidation,
            useLoading,
            useValue,
            useEmpty,
            useData,
            useLabel,
            useGenericName,
            useView,
            useTemplates,
            useClasses,
            useColumns,
            useSlots,
            useFocused,
            useHandleBlur,
            useA11y,
            useWatchValue,
            useFocus,
            useHandleKeyEvents,
            usePlaceholder,
        ]

        context.slots = [
            'label', 'info', 'required', 'description', 'before',
            'between', 'after',
        ]
        return {
            ...useElement(props, context)
        }
    }
})
</script>
